/**
 * Detect and recover from Claude Agent SDK "silent decline" after tools.
 *
 * After long-running MCP tools (e.g. Devin), the SDK can reconstruct the
 * turn as interrupted and inject a meta prompt:
 *   "Continue from where you left off."
 * Fable (and some other models) then emit the stop-sequence text
 *   "No response requested."
 * and the query ends. Relay would emit `complete`, drop isProcessing, and
 * look idle even though the original task is unfinished.
 */

export const SDK_SILENT_DECLINE_TEXT = 'No response requested.';

export const SILENT_CONTINUE_PROMPT =
  'The previous turn stopped after tools completed without finishing the task. ' +
  'Continue the original work now. Do not reply with "No response requested."';

/**
 * Default tool-result the Claude CLI writes when a call is cancelled because
 * the turn's abort signal is already tripped (background-task resume, input
 * close, sibling stop). It is not a permission decision. SDK 0.3.281 uses it
 * for almost every abort reason, so the model treats an interruption as
 * "the user declined" and stops.
 */
export const SDK_TOOL_CANCELLATION_TEXT =
  "The user doesn't want to take this action right now. STOP what you are doing and wait for the user to tell you how to proceed.";

export const TOOL_CANCELLATION_DISPLAY =
  'This action was interrupted before it finished. You did not decline it.';

export const CANCEL_CONTINUE_PROMPT =
  'Some tool calls in the previous turn were cancelled by a session interruption. ' +
  'The result "The user doesn\'t want to take this action right now" is not a user decision and not a reason to stop. ' +
  'Continue the original work now. Retry any cancelled action that is still needed.';

export function isSilentDeclineText(text: string | undefined | null): boolean {
  return (text ?? '').trim() === SDK_SILENT_DECLINE_TEXT;
}

export function isToolCancellationText(text: string | undefined | null): boolean {
  return (text ?? '').trimStart().startsWith(SDK_TOOL_CANCELLATION_TEXT);
}

/** User-facing replacement for the SDK's false "user declined" cancellation. */
export function clarifyToolCancellation(text: string | undefined): string | undefined {
  if (text == null) return text;
  return isToolCancellationText(text) ? TOOL_CANCELLATION_DISPLAY : text;
}

export function shouldResumeAfterSilentDecline(opts: {
  sawSilentDecline: boolean;
  hadToolActivity: boolean;
  alreadyContinued: boolean;
}): boolean {
  return opts.sawSilentDecline && opts.hadToolActivity && !opts.alreadyContinued;
}

export function shouldResumeAfterToolCancellation(opts: {
  sawCancellation: boolean;
  alreadyContinued: boolean;
}): boolean {
  return opts.sawCancellation && !opts.alreadyContinued;
}
